// Error codes the UI understands. Backend errors are mapped onto these.
export const COACHING_TOKEN_ERROR_CODES = Object.freeze({
  UNAUTHORIZED: 'UNAUTHORIZED', // integration not active for the company, or no fleetId configured
  NOT_READY: 'NOT_READY', // the Coaching roles or tags of the company are not synced yet
  NO_TAGS: 'NO_TAGS', // the sub user has no tags that exist in Coaching, so nothing could be shown
  CUSTOM_ERROR: 'CUSTOM_ERROR', // token could not be created; retrying may help
  EMPTY_TOKEN: 'EMPTY_TOKEN', // response resolved without errors but with a null/empty token
  UNKNOWN: 'UNKNOWN', // network failure or unexpected error
});

export class CoachingTokenError extends Error {
  constructor(code, message, cause) {
    super(message || code);
    this.name = 'CoachingTokenError';
    this.code = code;
    this.cause = cause;
  }
}

// clientMutation rejects with { error, innerError: ApolloError, errorData: rawResponse }.
// The GraphQL error can live in either place depending on how Apollo surfaced it.
const extractGraphQLError = (err) =>
  err?.innerError?.graphQLErrors?.[0] || err?.errorData?.errors?.[0] || null;

// The API answers every CustomException with errorType 'CustomException' and the framework
// ErrorCode in errorInfo.code (UNAUTHORIZED = 401, CUSTOM_ERROR = 1001). Older resolvers may
// put the code name in errorType itself, so both are accepted.
const CODE_BY_ERROR_INFO = {
  401: COACHING_TOKEN_ERROR_CODES.UNAUTHORIZED,
  1001: COACHING_TOKEN_ERROR_CODES.CUSTOM_ERROR,
};

// UNAUTHORIZED covers three situations the user should tell apart; the API message says which.
const UNAUTHORIZED_REASONS = [
  [/no tags/i, COACHING_TOKEN_ERROR_CODES.NO_TAGS],
  [/not ready/i, COACHING_TOKEN_ERROR_CODES.NOT_READY],
];

const resolveCode = (graphQLError) => {
  const infoCode = Number(graphQLError?.errorInfo?.code);
  const code =
    COACHING_TOKEN_ERROR_CODES[graphQLError?.errorType] ||
    CODE_BY_ERROR_INFO[infoCode] ||
    COACHING_TOKEN_ERROR_CODES.UNKNOWN;

  if (code !== COACHING_TOKEN_ERROR_CODES.UNAUTHORIZED) return code;

  const message = graphQLError?.message || '';
  const reason = UNAUTHORIZED_REASONS.find(([pattern]) => pattern.test(message));
  return reason ? reason[1] : code;
};

export const normalizeCoachingTokenError = (err) => {
  if (err instanceof CoachingTokenError) return err;

  const graphQLError = extractGraphQLError(err);
  const code = graphQLError ? resolveCode(graphQLError) : COACHING_TOKEN_ERROR_CODES.UNKNOWN;
  const message = graphQLError?.message || err?.error || err?.message;

  return new CoachingTokenError(code, message, err);
};

const MESSAGE_KEY_BY_CODE = {
  [COACHING_TOKEN_ERROR_CODES.UNAUTHORIZED]: 'coachSSO_error_unauthorized',
  [COACHING_TOKEN_ERROR_CODES.NOT_READY]: 'coachSSO_error_notReady',
  [COACHING_TOKEN_ERROR_CODES.NO_TAGS]: 'coachSSO_error_noTags',
  [COACHING_TOKEN_ERROR_CODES.CUSTOM_ERROR]: 'coachSSO_error_customError',
  [COACHING_TOKEN_ERROR_CODES.EMPTY_TOKEN]: 'coachSSO_error_customError',
  [COACHING_TOKEN_ERROR_CODES.UNKNOWN]: 'coachSSO_error_generic',
};

export const errorCodeToMessageKey = (code) =>
  MESSAGE_KEY_BY_CODE[code] || MESSAGE_KEY_BY_CODE[COACHING_TOKEN_ERROR_CODES.UNKNOWN];
